import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown, Sparkles, Lightbulb, BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getSlot } from "@/lib/brandAssets";

/**
 * Public navbar — horizontal logo, grouped dropdowns, "Explore Ideas" CTA,
 * and a slide-in side drawer on mobile.
 */
const NAV_GROUPS = [
  {
    key: "explore",
    label: "Explore",
    items: [
      { to: "/services", label: "Services", desc: "Consulting + co-publication packages" },
      { to: "/publications", label: "Publications", desc: "Papers we've helped publish" },
      { to: "/journals", label: "Journals & Conferences", desc: "Searchable database of publication venues" },
      { to: "/blogs", label: "Journal", desc: "Field notes, essays, and deep-dives" },
      { to: "/events", label: "Events & Workshops", desc: "Webinars, hands-on labs" },
      { to: "/discussions", label: "Discussions", desc: "Researcher-to-researcher forum" },
    ],
  },
  {
    key: "premium",
    label: "Premium",
    premium: true,
    items: [
      { to: "/explainers", label: "Paper Explainers", desc: "Talking-head + audiobook breakdowns" },
      { to: "/datasets", label: "Datasets", desc: "Curated public + downloadable datasets" },
      { to: "/pricing", label: "Pricing & Plans", desc: "Free, Standard, Premium" },
    ],
  },
];

function GroupMenu({ group, active }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`nav-group-${group.key}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold transition-colors ${
          active ? "text-kb-purple" : "text-kb-ink-muted hover:text-kb-purple"
        }`}
        aria-expanded={open}
        data-testid={`nav-group-${group.key}-trigger`}
      >
        {group.premium && <Sparkles className="w-3.5 h-3.5 text-kb-purple" />}
        {group.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-2 w-72 z-50" data-testid={`nav-group-${group.key}-menu`}>
          <div className="bg-white border border-kb-divider rounded-md shadow-xl py-2">
            {group.premium && (
              <div className="px-4 py-2 border-b border-kb-divider kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> PREMIUM
              </div>
            )}
            {group.items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 hover:bg-kb-subtle transition-colors"
                data-testid={`nav-item-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
              >
                <div className="text-sm font-semibold text-kb-ink">{it.label}</div>
                {it.desc && <div className="text-xs text-kb-ink-muted mt-0.5">{it.desc}</div>}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth() || {};

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Auto-close drawer on navigation so links don't leave it stuck open.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isGroupActive = (g) => g.items.some((it) => location.pathname.startsWith(it.to));
  const ideasActive = location.pathname.startsWith("/research-ideas");
  const logoSrc = getSlot("logo.horizontal.dark");

  return (
    <header
      data-testid="public-navbar"
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-kb-divider shadow-sm"
          : "bg-white md:bg-white/40 md:backdrop-blur-sm border-b border-kb-divider md:border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center shrink-0"
          data-testid="brand-home-link"
          onClick={(e) => {
            // If already on the landing page, just scroll to top instead of
            // triggering a no-op route change (React Router preserves scroll).
            if (location.pathname === "/") {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          <img
            src={logoSrc}
            alt="KhojByte Research Lab"
            className="h-7 md:h-8 w-auto select-none"
            draggable="false"
            data-testid="brand-logo-img"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_GROUPS.map((g) => (
            <GroupMenu key={g.key} group={g} active={isGroupActive(g)} />
          ))}
          <NavLink
            to="/research-ideas"
            data-testid="nav-explore-ideas-link"
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-colors ${
              ideasActive ? "text-kb-purple" : "text-kb-ink-muted hover:text-kb-purple"
            }`}
          >
            <Lightbulb className="w-3.5 h-3.5" />
            Explore Ideas
          </NavLink>
          <NavLink
            to="/blogs"
            data-testid="nav-blogs-link"
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-colors ${
                isActive ? "text-kb-purple" : "text-kb-ink-muted hover:text-kb-purple"
              }`
            }
          >
            <BookOpen className="w-3.5 h-3.5" />
            Journal
          </NavLink>
          <NavLink
            to="/about"
            data-testid="nav-about-link"
            className={({ isActive }) =>
              `px-3 py-2 text-sm font-semibold transition-colors ${
                isActive ? "text-kb-purple" : "text-kb-ink-muted hover:text-kb-purple"
              }`
            }
          >
            About
          </NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link to="/app" className="kb-btn-primary text-sm kb-dashboard-pulse" data-testid="navbar-dashboard-btn">
                <Sparkles className="w-3.5 h-3.5" />
                Dashboard
              </Link>
              <button
                onClick={async () => { await logout?.(); navigate("/", { replace: true }); }}
                className="text-sm font-semibold text-kb-ink-muted hover:text-kb-purple"
                data-testid="navbar-logout-btn"
              >
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-semibold text-kb-ink-muted hover:text-kb-purple" data-testid="navbar-login-link">
                Sign in
              </Link>
              <Link to="/onboarding/role" className="kb-btn-primary text-sm" data-testid="navbar-register-btn">
                Get started <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-kb-ink"
          data-testid="navbar-mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile side drawer — rendered into <body> via Portal to escape the
          backdrop-blur containing-block on <header>, which was clipping the
          drawer's `position:fixed` to just the 64px-tall header strip. */}
      {createPortal(
        <div
          className={`md:hidden fixed inset-0 z-[100] transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
          aria-hidden={!open}
        >
        <div
          className="absolute inset-0 bg-kb-dark/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="mobile-drawer-backdrop"
        />
        <aside
          className={`absolute right-0 top-0 bottom-0 w-[82%] max-w-[340px] bg-white shadow-2xl border-l border-kb-divider transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
          data-testid="mobile-drawer"
        >
          <div className="h-16 flex items-center justify-between px-5 border-b border-kb-divider">
            <img src={logoSrc} alt="KhojByte" className="h-6 w-auto" />
            <button onClick={() => setOpen(false)} className="p-2 text-kb-ink" data-testid="mobile-drawer-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="px-5 py-5 overflow-y-auto h-[calc(100%-4rem)] space-y-5">
            <Link
              to="/research-ideas"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between w-full px-4 py-3 rounded-lg bg-kb-subtle text-kb-purple font-semibold text-sm"
              data-testid="mobile-nav-explore-ideas"
            >
              <span className="inline-flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Explore Ideas</span>
              <ArrowRight className="w-4 h-4" />
            </Link>

            {NAV_GROUPS.map((g) => (
              <div key={g.key}>
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted py-1">
                  {g.premium && <Sparkles className="w-3 h-3 inline mr-1 text-kb-purple" />}
                  {g.label}
                </div>
                <div className="mt-1 space-y-1">
                  {g.items.map((it) => (
                    <Link
                      key={it.to}
                      to={it.to}
                      onClick={() => setOpen(false)}
                      className="block py-2 pl-1 text-sm text-kb-ink hover:text-kb-purple"
                      data-testid={`mobile-nav-${it.to.replace(/\//g, "-").replace(/^-/, "")}`}
                    >
                      {it.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            <div className="pt-1 space-y-1">
              <Link to="/about" onClick={() => setOpen(false)} className="block py-2 text-sm font-semibold text-kb-ink">About</Link>
            </div>

            <div className="pt-4 border-t border-kb-divider flex flex-col gap-2">
              {user ? (
                <>
                  <Link to="/app" className="kb-btn-primary justify-center kb-dashboard-pulse" onClick={() => setOpen(false)}>
                    <Sparkles className="w-4 h-4" /> Go to Dashboard
                  </Link>
                  <button
                    onClick={async () => { setOpen(false); await logout?.(); navigate("/", { replace: true }); }}
                    className="kb-btn-outline justify-center"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="kb-btn-outline justify-center" onClick={() => setOpen(false)}>Sign in</Link>
                  <Link to="/onboarding/role" className="kb-btn-primary justify-center" onClick={() => setOpen(false)}>Get started</Link>
                </>
              )}
            </div>
          </div>
        </aside>
        </div>,
        document.body
      )}
      </header>
  );
}
